import * as React from 'react';
import { useState, useMemo } from 'react';
import Markdown from 'react-markdown';
import { cleanDecimalFormatting } from './utils';

interface Props {
  content: string;
  expandable?: boolean;
}

/**
 * Markdown renderer for AI-generated summaries using react-markdown.
 * Handles: headers, bold text, bullet points, paragraphs, and more.
 * Also cleans up decimal formatting (e.g., 5.0 → 5).
 */
const MarkdownRenderer: React.FC<Props> = ({ content, expandable = true }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Clean up decimal formatting (e.g., 3.5/5.0 → 3.5/5)
  const cleanedContent = useMemo(() => cleanDecimalFormatting(content), [content]);

  // For expandable mode, we need to count content blocks and potentially truncate
  // We'll split by double newlines to get rough paragraph/section count
  const contentBlocks = cleanedContent.split(/\n\n+/).filter((block) => block.trim());
  const shouldTruncate = expandable && contentBlocks.length > 3;
  const displayContent =
    shouldTruncate && !isExpanded ? contentBlocks.slice(0, 3).join('\n\n') : cleanedContent;

  return (
    <div className="prose prose-sm max-w-none">
      <Markdown
        components={{
          // Headers
          h1: ({ children }) => (
            <h1 className="text-base font-semibold text-gray-900 mt-4 mb-2 first:mt-0">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-sm font-semibold text-gray-900 mt-4 mb-2 first:mt-0">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-sm font-semibold text-gray-900 mt-4 mb-2 first:mt-0">{children}</h3>
          ),
          // Paragraphs
          p: ({ children }) => (
            <p className="text-sm text-gray-700 leading-relaxed mb-3">{children}</p>
          ),
          // Bold text
          strong: ({ children }) => (
            <strong className="font-semibold text-gray-900">{children}</strong>
          ),
          // Italic text
          em: ({ children }) => <em className="italic">{children}</em>,
          // Lists
          ul: ({ children }) => <ul className="list-disc ml-6 mb-5 space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal ml-6 mb-5 space-y-1">{children}</ol>,
          li: ({ children }) => (
            <li className="text-sm text-gray-700 leading-relaxed mb-1">{children}</li>
          ),
          // Links
          a: ({ href, children }) => (
            <a
              href={href}
              className="text-blue-600 hover:text-blue-800 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {children}
            </a>
          ),
          // Code
          code: ({ children }) => (
            <code className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-xs font-mono">
              {children}
            </code>
          ),
          // Block quotes
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-600 my-3">
              {children}
            </blockquote>
          ),
        }}
      >
        {displayContent}
      </Markdown>

      {shouldTruncate && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-3 text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          {isExpanded ? 'See less' : 'See more'}
        </button>
      )}
    </div>
  );
};

export default MarkdownRenderer;
